import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, User, Clock, Shield, Smartphone } from 'lucide-react';
import { useSignedStorageUrl } from '@/hooks/useSignedStorageUrl';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import type { Invoice } from '@/types/invoice';

export const JOB_SIGNATURES_BUCKET = 'job-signatures';

interface InvoiceSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Pick<Invoice, 'invoice_number' | 'signature_path' | 'signed_at' | 'signer_name' | 'signature_consent'> | null;
}

/**
 * Customer signature captured on the technician's device when the job was
 * closed from the Steddy app (invoices.signature_path → private bucket
 * job-signatures, SVG). Mirrors the estimate ViewSignatureDialog.
 */
export function InvoiceSignatureDialog({ open, onOpenChange, invoice }: InvoiceSignatureDialogProps) {
  const { locale, timezone } = useCompanyLocale();
  const { data: url, isLoading, error } = useSignedStorageUrl(JOB_SIGNATURES_BUCKET, open ? invoice?.signature_path : null);

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-success" />
            Customer signature
          </DialogTitle>
          <DialogDescription>
            Signed on the technician's device for invoice {invoice.invoice_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Signature</p>
            <div className="rounded-lg border bg-white p-2">
              {isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : url ? (
                <img src={url} alt="Customer signature" className="max-h-32 w-full object-contain" />
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {error ? 'The signature file could not be loaded.' : 'No signature file on record.'}
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid gap-3">
            <div className="flex items-start gap-3">
              <User className="mt-1 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Signed by</p>
                <p className="text-sm text-muted-foreground">{invoice.signer_name || 'Unknown'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="mt-1 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Signed at</p>
                <p className="text-sm text-muted-foreground">
                  {invoice.signed_at ? formatDocDate(invoice.signed_at, `${locale.longDate} 'at' h:mm a`, timezone) : 'Unknown'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Smartphone className="mt-1 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Captured</p>
                <p className="text-sm text-muted-foreground">On the technician's device, in the Steddy app</p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Shield className="h-4 w-4" />
              Consent
            </p>
            {invoice.signature_consent ? (
              <Badge variant="outline" className="border-success/20 bg-success/10 text-success">
                <CheckCircle className="mr-1 h-3 w-3" />
                Customer confirmed the work and agreed to the invoice
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">Consent not recorded</Badge>
            )}
          </div>

          <div className="rounded-lg bg-muted/50 p-3">
            <p className="text-xs text-muted-foreground">
              The signature image, signer name and time were recorded when the technician closed the job. They are
              write-once and cannot be edited from the CRM.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
